import { Fragment, useMemo } from 'react';
import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import { TextInput, AutocompleteInput } from '../inputs';
import { useSelector, systemSelectors } from '../../../state';
import { requiresStateCode, stringCapitalize } from '../../../lib';
import { SelectOption } from '../../../types';

interface ContactFieldsProps {
  formikProps: FormikProps<any>;
  inputClassName: string;
  inputNamePrefix?: string;
  addressFieldName?: string;
  addressFieldFullWidth?: boolean;
  hasMultipleAddresses?: boolean;
  idUpperCase?: boolean;
  includePhone?: boolean;
  includeEmail?: boolean;
}

export function ContactFields({
  formikProps,
  inputClassName,
  inputNamePrefix = '',
  addressFieldName = 'address',
  addressFieldFullWidth = false,
  hasMultipleAddresses = false,
  idUpperCase,
  includePhone,
  includeEmail,
}: ContactFieldsProps) {
  return (
    <Grid container spacing={5} rowSpacing={2}>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <TextInput
          name={getFieldName('companyName', inputNamePrefix)}
          formikProps={formikProps}
          label='Company name'
          className={inputClassName}
        />
      </Grid>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <TextInput
          name={getFieldName('firstName', inputNamePrefix)}
          formikProps={formikProps}
          label='First name'
          className={inputClassName}
        />
      </Grid>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <TextInput
          name={getFieldName('lastName', inputNamePrefix)}
          formikProps={formikProps}
          label='Last name'
          className={inputClassName}
        />
      </Grid>
      {includePhone && (
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: 6,
          }}
        >
          <TextInput
            name={getFieldName('officePhone', inputNamePrefix)}
            formikProps={formikProps}
            label='Phone'
            className={inputClassName}
            format='phone'
          />
        </Grid>
      )}
      {includeEmail && (
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: 12,
          }}
        >
          <TextInput
            name={getFieldName('email', inputNamePrefix)}
            formikProps={formikProps}
            label='Email'
            className={inputClassName}
          />
        </Grid>
      )}
      <ContactAddressFields
        formikProps={formikProps}
        inputClassName={inputClassName}
        inputNamePrefix={inputNamePrefix}
        addressFieldName={addressFieldName}
        addressFieldFullWidth={addressFieldFullWidth}
        hasMultipleAddresses={hasMultipleAddresses}
        idUpperCase={idUpperCase}
      />
    </Grid>
  );
}

export function ContactAddressFields({
  formikProps,
  inputClassName,
  inputNamePrefix = '',
  addressFieldName = 'address',
  addressFieldFullWidth = false,
  idUpperCase,
  hasMultipleAddresses = false,
}: ContactFieldsProps) {
  const countries = useSelector(systemSelectors.countries);
  const countriesObj = useSelector(systemSelectors.countriesObj);
  const states = useSelector(systemSelectors.states);
  const countryName = getFieldName(
    `${idUpperCase ? 'countryID' : 'countryId'}`,
    inputNamePrefix,
  );
  const countryId = formikProps.values[countryName];
  const stateName = getFieldName('stateAlternate', inputNamePrefix);
  const stateAlternate = formikProps.values[stateName];

  const showAlternateStateInput = useMemo(() => {
    return !requiresStateCode({
      countryId,
      stateAlternate,
      countriesObj,
    });
  }, [countriesObj, countryId, stateAlternate]);

  return (
    <Fragment>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: addressFieldFullWidth ? 12 : 6,
        }}
      >
        <TextInput
          name={getFieldName(addressFieldName, inputNamePrefix)}
          formikProps={formikProps}
          label='Address'
          className={inputClassName}
        />
      </Grid>
      {hasMultipleAddresses && (
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: addressFieldFullWidth ? 12 : 6,
          }}
        >
          <TextInput
            name={getFieldName('address2', inputNamePrefix)}
            formikProps={formikProps}
            label='Address 2'
            className={inputClassName}
          />
        </Grid>
      )}
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <TextInput
          name={getFieldName('city', inputNamePrefix)}
          formikProps={formikProps}
          label='City'
          className={inputClassName}
        />
      </Grid>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        {showAlternateStateInput ? (
          <TextInput
            name={stateName}
            formikProps={formikProps}
            label='Enter state'
            className={inputClassName}
          />
        ) : (
          <AutocompleteInput
            formikProps={formikProps}
            textFieldProps={{
              name: getFieldName(
                `${idUpperCase ? 'stateID' : 'stateId'}`,
                inputNamePrefix,
              ),
              label: 'State',
              autoComplete: 'new-password',
            }}
            autocompleteProps={{
              options: states as SelectOption<string | number>[],
            }}
          />
        )}
      </Grid>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <TextInput
          name={getFieldName('zip', inputNamePrefix)}
          formikProps={formikProps}
          label='Zip'
          className={inputClassName}
        />
      </Grid>
      <Grid
        style={{ paddingBottom: 0 }}
        size={{
          sm: 6,
        }}
      >
        <AutocompleteInput
          formikProps={formikProps}
          textFieldProps={{
            name: countryName,
            label: 'Country',
            autoComplete: 'new-password',
          }}
          autocompleteProps={{
            options: countries,
          }}
        />
      </Grid>
    </Fragment>
  );
}

function getFieldName(field, inputNamePrefix) {
  if (!inputNamePrefix) {
    return field;
  }

  return inputNamePrefix + stringCapitalize(field);
}
